/**
 * Shared API to Postman Export Functionality
 * This module provides common functions for exporting API documentation to Postman collections
 * Used by both EditorJS custom API tool and markdown-rendered API blocks
 */

window.ApiPostmanExporter = {
    /**
     * Generate a Postman collection from API data
     */
    generatePostmanCollection(apiData) {
        const collectionName = apiData.title || 'API Endpoint';
        const fullUrl = (apiData.baseUrl || '').replace(/\/$/, '') + '/' + (apiData.endpoint || '').replace(/^\//, '');

        // Build request headers
        const headers = [];
        (apiData.headers || []).forEach(header => {
            if (header.key && header.key.trim()) {
                headers.push({
                    key: header.key,
                    value: header.value || '',
                    type: 'text'
                });
            }
        });

        // Build query parameters
        const queryParams = [];
        (apiData.queryParams || []).forEach(param => {
            if (param.key && param.key.trim()) {
                queryParams.push({
                    key: param.key,
                    value: param.value || '',
                    description: param.value || '',
                    disabled: false
                });
            }
        });

        // Build request body
        let requestBody = null;
        if (apiData.bodyType === 'json' && apiData.requestBody && apiData.requestBody.trim()) {
            requestBody = {
                mode: 'raw',
                raw: apiData.requestBody,
                options: {
                    raw: {
                        language: 'json'
                    }
                }
            };
        } else if (apiData.bodyType === 'form' && apiData.formData && apiData.formData.length > 0) {
            const formdata = [];
            apiData.formData.forEach(field => {
                if (field.key && field.key.trim()) {
                    formdata.push({
                        key: field.key,
                        value: field.value || '',
                        type: 'text'
                    });
                }
            });
            requestBody = {
                mode: 'formdata',
                formdata: formdata
            };
        } else if (apiData.bodyType === 'raw' && apiData.rawBody && apiData.rawBody.trim()) {
            requestBody = {
                mode: 'raw',
                raw: apiData.rawBody
            };
        }

        // Build response examples
        const responseExamples = [];
        if (apiData.responses) {
            Object.keys(apiData.responses).forEach(statusCode => {
                const response = apiData.responses[statusCode];
                if (response && response.body) {
                    responseExamples.push({
                        name: `${statusCode} ${response.description || 'Response'}`,
                        status: response.description || 'Response',
                        code: parseInt(statusCode),
                        _postman_previewlanguage: 'json',
                        header: [
                            {
                                key: 'Content-Type',
                                value: 'application/json'
                            }
                        ],
                        cookie: [],
                        body: response.body
                    });
                }
            });
        }

        // Parse URL for Postman format
        const urlObj = new URL(apiData.baseUrl || 'https://api.example.com');

        // Build the main request object
        const request = {
            method: apiData.method || 'GET',
            header: headers,
            url: {
                raw: fullUrl,
                protocol: urlObj.protocol.replace(':', ''),
                host: urlObj.hostname.split('.'),
                port: urlObj.port || '',
                path: (apiData.endpoint || '').split('/').filter(p => p !== ''),
                query: queryParams
            }
        };

        if (requestBody) {
            request.body = requestBody;
        }

        // Build the complete Postman collection
        return {
            info: {
                _postman_id: this.generateUuid(),
                name: collectionName,
                description: apiData.description || `API documentation for ${collectionName}`,
                schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json'
            },
            item: [
                {
                    name: collectionName,
                    request: request,
                    response: responseExamples
                }
            ]
        };
    },

    /**
     * Generate a simple UUID for Postman collection
     */
    generateUuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    },

    /**
     * Sanitize filename for download
     */
    sanitizeFilename(filename) {
        return filename.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').toLowerCase();
    },

    /**
     * Show export feedback using Filament notifications
     */
    showExportFeedback(message, type) {
        // Use Filament notification for better UX
        const notification = new window.Notification()
            .title(type === 'success' ? 'Export successful' : 'Export failed')
            .body(message)
            .icon(type === 'success' ? 'heroicon-o-document-arrow-down' : 'heroicon-o-exclamation-triangle');

        if (type === 'success') {
            notification.success();
        } else {
            notification.danger();
        }

        notification.send();
    },

    /**
     * Export API data to Postman collection file
     */
    exportToPostman(apiData, customFilename = null) {
        try {
            const collection = this.generatePostmanCollection(apiData);
            const jsonString = JSON.stringify(collection, null, 2);

            // Create download
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            const filename = customFilename || `${this.sanitizeFilename(apiData.title || 'API Endpoint')}.postman_collection.json`;

            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Show success feedback
            this.showExportFeedback(`API documentation exported as "${filename}"`, 'success');
        } catch (error) {
            console.error('Export to Postman failed:', error);
            this.showExportFeedback('Unable to export API documentation. Please check your data and try again.', 'error');
        }
    }
};

/**
 * Global function for markdown-rendered API blocks
 * This maintains backward compatibility with existing onclick handlers
 */
window.exportApiToPostman = function (apiId) {
    if (!window.apiBlockData || !window.apiBlockData[apiId]) {
        console.error('API data not found for ID:', apiId);
        window.ApiPostmanExporter.showExportFeedback('API data not found. Please reload the page and try again.', 'error');
        return;
    }

    const apiData = window.apiBlockData[apiId];
    window.ApiPostmanExporter.exportToPostman(apiData);
}; 